import React, { useCallback, useEffect, useRef, useState } from "react";
import "./Gallery.scss";
import NavBar from "../../shared/components/NavBar/NavBar";
import Footer from "../../shared/components/Footer/Footer";
import PageHeader from "../../shared/components/PageHeader/PageHeader";
import { galleryPhotos } from "../../shared/gallery";

const BASE = `${process.env.PUBLIC_URL}/gallery`;

const formatDate = (iso: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

// Photos grouped by year, keeping chronological order.
const years = galleryPhotos.reduce<{ year: string; items: number[] }[]>(
  (groups, photo, index) => {
    const year = photo.date.slice(0, 4);
    const last = groups[groups.length - 1];
    if (last && last.year === year) last.items.push(index);
    else groups.push({ year, items: [index] });
    return groups;
  },
  []
);

const Gallery: React.FC = () => {
  const [open, setOpen] = useState<number | null>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const lastTrigger = useRef<HTMLButtonElement | null>(null);

  const close = useCallback(() => {
    setOpen(null);
    lastTrigger.current?.focus();
  }, []);
  const step = useCallback(
    (delta: number) =>
      setOpen((i) =>
        i === null ? i : (i + delta + galleryPhotos.length) % galleryPhotos.length
      ),
    []
  );

  useEffect(() => {
    if (open === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, close, step]);

  const current = open === null ? null : galleryPhotos[open];

  return (
    <div className="ss-gallery fade-in">
      <NavBar active="gallery" />
      <PageHeader
        crumb="Gallery"
        eyebrow="Gallery"
        title="Moments from Skill Surf"
        lead="Labs, learner-built boards, tapeouts and medals: a look back at our journey, from the start to today."
      />

      <section className="ss-section">
        <div className="ss-container ss-gallery__timeline">
          {years.map((group) => (
            <div className="ss-gallery__year" key={group.year}>
              <div className="ss-gallery__year-label">
                <span>{group.year}</span>
                <small>
                  {group.items.length} {group.items.length === 1 ? "photo" : "photos"}
                </small>
              </div>
              <div className="ss-gallery__grid">
                {group.items.map((index) => {
                  const photo = galleryPhotos[index];
                  return (
                    <figure className="ss-gallery__item" key={photo.file}>
                      <button
                        type="button"
                        onClick={(e) => {
                          lastTrigger.current = e.currentTarget;
                          setOpen(index);
                        }}
                        aria-label={`Open photo: ${photo.caption}`}
                      >
                        <img
                          src={`${BASE}/thumb/${photo.file}`}
                          alt={photo.caption}
                          width={photo.width}
                          height={photo.height}
                          loading="lazy"
                          decoding="async"
                        />
                      </button>
                      <figcaption>
                        <time dateTime={photo.date}>{formatDate(photo.date)}</time>
                        {photo.caption}
                      </figcaption>
                    </figure>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

      {current && open !== null && (
        <div
          className="ss-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={current.caption}
          onClick={close}
        >
          <button
            ref={closeRef}
            type="button"
            className="ss-lightbox__close"
            onClick={close}
            aria-label="Close"
          >
            <i className="bi bi-x-lg" aria-hidden="true"></i>
          </button>
          <button
            type="button"
            className="ss-lightbox__nav ss-lightbox__nav--prev"
            onClick={(e) => {
              e.stopPropagation();
              step(-1);
            }}
            aria-label="Previous photo"
          >
            <i className="bi bi-chevron-left" aria-hidden="true"></i>
          </button>
          <figure className="ss-lightbox__figure" onClick={(e) => e.stopPropagation()}>
            <img
              key={current.file}
              src={`${BASE}/full/${current.file}`}
              alt={current.caption}
            />
            <figcaption>
              <time dateTime={current.date}>{formatDate(current.date)}</time>
              <span>{current.caption}</span>
              <small>
                {open + 1} / {galleryPhotos.length}
              </small>
            </figcaption>
          </figure>
          <button
            type="button"
            className="ss-lightbox__nav ss-lightbox__nav--next"
            onClick={(e) => {
              e.stopPropagation();
              step(1);
            }}
            aria-label="Next photo"
          >
            <i className="bi bi-chevron-right" aria-hidden="true"></i>
          </button>
        </div>
      )}

      <Footer isFinancialInquiries={true} />
    </div>
  );
};

export default Gallery;
